import {datetimeFormatter, getUtcOffset, Typography} from '@hconnect/uikit'
import {Timeline, TimelineElementProps} from '@hm/fundamental'
import CloseIcon from '@mui/icons-material/Close'
import {Button, Theme} from '@mui/material'
import {TFunction} from 'i18next'
import {useTranslation} from 'react-i18next'
import {makeStyles} from 'tss-react/mui'

import {PageNames} from '../../../../common/constants'
import {BusinessLineType} from '../../../../common/types'
import {ETADeliveryTrucks} from '../../../../Molecules/ETADeliveryTrucks/ETADeliveryTrucks'
import {useFeaturesState} from '../../../../Organisms/Features'
import {usePermissions} from '../../../../Permissions'
import {Delivery, DeliveryEventsAccuracy} from '../../../Finance/Invoices/Delivery.types'
import {TimezoneWarning} from '../../components'
import {
  DeliveryStatus,
  DeliveryTicketNumber,
  DeliveryVehicle,
  PricingInformation
} from '../../components/DeliveryFields'
import {QuantityUomToSymbolComponent} from '../../Order.components'
import {Order} from '../../Order.types'
import {getEventLocationOffset} from '../../Order.utils'

import {DetailsField} from './DetailsField'
import {getDeliveryToggles} from './utils'

interface DeliveryDetailsProps {
  delivery: Delivery
  order: Order
  close: () => void
  analytics?: any
  customerId: string
}

const useStyles = makeStyles()((theme: Theme) => ({
  cardTitle: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: '#54708C'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2)
  }
}))

const createDeliveryTimelineItems = (
  delivery: Delivery,
  t: TFunction,
  language: string
): TimelineElementProps[] => {
  // Return empty array if no delivery events or all events are null/undefined
  if (!delivery.deliveryEvents || Object.keys(delivery.deliveryEvents).length === 0) {
    return []
  }

  const eventLabels: Record<string, string> = {
    loadStarted: t('order.expand.deliveryEvents.loadStarted'),
    batchStarted: t('order.expand.deliveryEvents.batchStarted'),
    loadFinished: t('order.expand.deliveryEvents.loadFinished'),
    plantLeft: t('order.expand.deliveryEvents.plantLeft'),
    arrived: t('order.expand.deliveryEvents.arrived'),
    unloadStarted: t('order.expand.deliveryEvents.unloadStarted'),
    unloadFinished: t('order.expand.deliveryEvents.unloadFinished'),
    deliveryNotePrinted: t('order.expand.deliveryEvents.deliveryNotePrinted'),
    leftCustomer: t('order.expand.deliveryEvents.leftCustomer')
  }

  const items: TimelineElementProps[] = []
  const eventsWithTime: Array<{key: string; time: string}> = []

  // Collect events with their timestamps
  Object.entries(delivery.deliveryEvents).forEach(([eventKey, eventTime]) => {
    if (eventTime) {
      eventsWithTime.push({key: eventKey, time: eventTime})
    }
  })

  // Sort by timestamp
  eventsWithTime.sort((a, b) => {
    const dateA = new Date(a.time).getTime()
    const dateB = new Date(b.time).getTime()
    return dateB - dateA
  })

  // Create timeline items
  eventsWithTime.forEach(({key: eventKey, time: eventTime}) => {
    const accuracy = delivery.deliveryEventsAccuracy?.[eventKey as keyof DeliveryEventsAccuracy]
    const isEstimated = accuracy && accuracy !== 'actual' && accuracy !== 'unknown'

    let title = eventLabels[eventKey] || eventKey
    if (isEstimated) {
      title += '*'
    }

    items.push({
      time: datetimeFormatter(eventTime, language),
      title,
      'data-cy': `delivery-event-${eventKey}`
    })
  })

  // Set positions
  items.forEach((item, index) => {
    if (items.length === 1) {
      item.position = 'Single step'
    } else if (index === 0) {
      item.position = 'Start'
    } else if (index === items.length - 1) {
      item.position = 'End'
    } else {
      item.position = 'Middle'
    }
  })

  return items
}

export const DeliveryDetails: React.FC<DeliveryDetailsProps> = ({
  delivery,
  order,
  close,
  analytics,
  customerId
}) => {
  const {classes} = useStyles()
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const features = useFeaturesState()
  const permissions = usePermissions()

  const toggles = getDeliveryToggles(features, permissions, order)
  const {eticket: {ageOfConcrete = 0} = {}} = delivery || {}

  const timelineItems = createDeliveryTimelineItems(delivery, t, language)

  // Check if any events are estimated
  const hasEstimatedEvents =
    delivery.deliveryEventsAccuracy &&
    Object.values(delivery.deliveryEventsAccuracy).some(
      (accuracy) => accuracy && accuracy !== 'actual' && accuracy !== 'unknown'
    )

  // Calculate timezone offsets for TimezoneWarning
  const userOffset = getUtcOffset(order.shippingDate || new Date().toISOString())
  const eventLocationOffset = getEventLocationOffset(order)

  return (
    <>
      <div className={classes.header}>
        <span className={classes.cardTitle}>{t('order.expand.deliveryDetails')}</span>
        <Button
          variant="text"
          size="large"
          onClick={() => close()}
          data-test-id="selected-orders-close"
        >
          <CloseIcon scale={2} sx={{color: '#00274D'}} />
        </Button>
      </div>
      <div className={classes.content}>
        <DeliveryStatus delivery={delivery} toggles={toggles} customerId={customerId} />
        <DetailsField
          label={t('order.expand.deliveryNumber')}
          dataTestId="ticket-number-field"
          value={
            <DeliveryTicketNumber
              delivery={delivery}
              toggles={toggles}
              customerId={customerId}
              analytics={analytics}
            />
          }
        />
        {timelineItems.length > 0 ? (
          <DetailsField
            label=""
            dataTestId="delivery-timeline"
            value={
              <>
                <TimezoneWarning
                  t={t}
                  order={order}
                  userOffset={userOffset}
                  eventLocationOffset={eventLocationOffset}
                />
                <Timeline items={timelineItems} />
                {hasEstimatedEvents && (
                  <Typography variant="caption" color="secondary">
                    * {t('order.expand.deliveryEvents.estimated')}
                  </Typography>
                )}
              </>
            }
          />
        ) : null}
        <DetailsField
          label={toggles.isRMC ? t('order.expand.quantity') : t('order.expand.qtyLoaded')}
          value={
            <>
              {delivery.loadQuantity}{' '}
              <QuantityUomToSymbolComponent uom={delivery.loadQuantityUom || ''} />
            </>
          }
          dataTestId="loadQuantity-value"
        />
        <DetailsField
          label={t(`order.expand.modeoftransport.${delivery.modeOfTransport || 'others'}`)}
          dataTestId="vehicle"
          value={
            <DeliveryVehicle
              delivery={delivery}
              customerId={customerId}
              toggles={toggles}
              analytics={analytics}
            />
          }
        />
        {delivery.returnedDelivery && (
          <>
            <DetailsField
              label={t('order.expand.returnedDeliveryQuantity')}
              value={
                <>
                  {delivery.returnedDelivery.quantity}{' '}
                  <QuantityUomToSymbolComponent uom={delivery.returnedDelivery.unit} />
                </>
              }
              dataTestId="returned-quantity-value"
            />
            <DetailsField
              label={t('order.expand.returnedDeliveryNumber')}
              dataTestId="returned-id"
              value={delivery.returnedDelivery.deliveryId?.split('.')[1]}
            />
          </>
        )}

        {delivery.pricing && (
          <DetailsField
            label={t('invoiceDetail.lineItems.expand.unitPrice')}
            value={<PricingInformation delivery={delivery} />}
            dataTestId="returned-quantity-value"
          />
        )}

        <DetailsField
          label={t('order.orderDetails.plantName')}
          dataTestId="delivery-plant-details"
          value={delivery.plantName || '-'}
        />

        {ageOfConcrete > 0 && (
          <DetailsField
            label={t('order.orderDetails.ageOfConcrete')}
            dataTestId="age-of-concrete"
            value={`${ageOfConcrete} ${t('order.orderDetails.minutes')}`}
            businessLine={order.businessLine}
            showForBusinessLines={[BusinessLineType.RMC]}
          />
        )}

        {order.isHaveMoreThanOne && (
          <DetailsField
            label={t('order.orderDetails.orderNumber')}
            dataTestId="delivery-sales-order-number"
            value={delivery.orderNumber}
          />
        )}
        <ETADeliveryTrucks
          t={t}
          delivery={delivery}
          order={order}
          pageName={PageNames.ORDER_DETAILS}
        />
      </div>
    </>
  )
}
